import { useState, useEffect } from 'react';
import { Car, Plus, Gauge } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Card from '../ui/Card';
import Select from '../ui/Select';
import { supabase } from '../../lib/supabase';
import { onlyDigits } from '../../utils/masks';
import { isValidPlate } from '../../utils/validators';
import { getVehicleYearOptions } from '../../utils/vehicleYears';
import { useLocalization } from '../../hooks/useLocalization';
import { distance } from '../../utils/formatters';

const marcas = [
  'Fiat', 'VW', 'Chevrolet', 'Ford', 'Toyota', 'Honda',
  'Hyundai', 'Renault', 'Nissan', 'Jeep'
];

const cores = [
  'Branco', 'Preto', 'Prata', 'Cinza', 'Vermelho',
  'Azul', 'Verde', 'Amarelo', 'Outra'
];

const anos = getVehicleYearOptions();

export default function VehicleManagementWithRegistration({
  officeId,
  clientId,
  onVehicleSelected,
  selectedVehicle,
  disabled = false
}) {
  const { settings } = useLocalization();
  const [vehicles, setVehicles] = useState([]);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const [newVehicle, setNewVehicle] = useState({
    placa: '',
    marca: '',
    marca_custom: '',
    modelo: '',
    ano: '',
    cor: '',
    km_atual: ''
  });

  // Load vehicles for the selected client
  useEffect(() => {
    if (!officeId || !clientId) {
      setVehicles([]);
      return;
    }

    const loadVehicles = async () => {
      const { data, error } = await supabase
        .from('veiculos')
        .select('id, placa, marca, modelo, ano, cor, km_atual')
        .eq('oficina_id', officeId)
        .is('removed_at', null)
        .eq('cliente_id', clientId)
        .order('modelo');

      if (!error) {
        setVehicles(data || []);
      }
    };

    loadVehicles();
  }, [officeId, clientId]);

  const handleVehicleSelect = (vehicle) => {
    onVehicleSelected(vehicle);
    setShowRegistrationForm(false);
  };

  const handleCreateVehicle = async () => {
    setFormError('');

    if (!officeId || !clientId) {
      const message = 'Selecione um cliente antes de cadastrar o veículo';
      setFormError(message);
      toast.error(message);
      return;
    }

    if (!newVehicle.placa.trim()) {
      toast.error('Placa é obrigatória');
      return;
    }

    if (!isValidPlate(newVehicle.placa, settings.countryCode)) {
      toast.error(
        settings.countryCode === 'BR'
          ? 'Informe uma placa válida.'
          : `Informe um ${settings.vehicleRegistrationLabel} válido.`
      );
      return;
    }

    const marcaFinal = newVehicle.marca === '__other__' ? newVehicle.marca_custom.trim() : newVehicle.marca;

    if (!marcaFinal) {
      toast.error('Marca é obrigatória');
      return;
    }

    if (!newVehicle.modelo.trim()) {
      toast.error('Modelo é obrigatório');
      return;
    }

    if (!newVehicle.ano) {
      toast.error('Ano é obrigatório');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('veiculos')
        .insert({
          oficina_id: officeId,
          cliente_id: clientId,
          placa: newVehicle.placa.toUpperCase().trim(),
          marca: marcaFinal,
          modelo: newVehicle.modelo.trim(),
          ano: parseInt(newVehicle.ano),
          cor: newVehicle.cor || null,
          km_atual: parseInt(newVehicle.km_atual) || null,
          odometro_unidade: settings.measurementSystem === 'imperial' ? 'mi' : 'km'
        })
        .select()
        .single();

      if (error) throw error;

      // Add to local vehicles list
      setVehicles(prev => [...prev, data]);

      // Auto-select new vehicle
      handleVehicleSelect(data);

      // Reset form
      setNewVehicle({ placa: '', marca: '', marca_custom: '', modelo: '', ano: '', cor: '', km_atual: '' });
      setFormError('');
      setShowRegistrationForm(false);

      toast.success('Veículo cadastrado com sucesso!');
    } catch (error) {
      console.error('Error creating vehicle:', error);
      const message =
        'Não foi possível cadastrar o veículo. Verifique os dados e tente novamente.';

      setFormError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Vehicle Selection */}
      {!disabled && vehicles.length > 0 && !showRegistrationForm && (
        <div>
          <label className="mb-2 block text-sm font-medium">Selecione o veículo</label>
          <div className="space-y-2">
            {vehicles.map(vehicle => (
              <Card
                key={vehicle.id}
                className={`cursor-pointer transition-colors ${selectedVehicle?.id === vehicle.id
                  ? 'border-accent bg-accent/5'
                  : 'hover:bg-surface2'
                  }`}
                onClick={() => handleVehicleSelect(vehicle)}
              >
                <div className="flex items-center gap-3">
                  <Car className="text-zinc-400" size={20} />
                  <div className="flex-1">
                    <div className="font-medium">{vehicle.modelo}</div>
                    <div className="text-sm text-zinc-400">
                      {vehicle.placa} • {vehicle.marca} • {vehicle.ano}
                    </div>
                  </div>
                  {vehicle.km_atual && (
                    <div className="text-sm text-zinc-400">
                      <Gauge size={14} className="inline mr-1" />
                      {distance(vehicle.km_atual)}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Selected Vehicle Display */}
      {selectedVehicle && !showRegistrationForm && (
        <Card className="bg-accent/5 border-accent/20">
          <div className="flex items-center gap-3">
            <Car className="text-accent" size={20} />
            <div>
              <div className="font-medium">{selectedVehicle.modelo}</div>
              <div className="text-sm text-zinc-400">
                {selectedVehicle.placa} • {selectedVehicle.marca} • {selectedVehicle.ano}
                {selectedVehicle.km_atual && ` • ${distance(selectedVehicle.km_atual)}`}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* No Vehicles - Show Add Vehicle Button */}
      {!disabled && !showRegistrationForm && vehicles.length === 0 && (
        <Card className="border-dashed border-border">
          <div className="text-center py-4">
            <Car size={32} className="mx-auto mb-2 text-zinc-400" />
            <p className="text-zinc-300 mb-3">Nenhum veículo encontrado</p>
            <Button
              onClick={() => setShowRegistrationForm(true)}
              variant="ghost"
              className="w-full"
            >
              <Plus size={16} className="mr-2" />
              Cadastrar novo veículo
            </Button>
          </div>
        </Card>
      )}

      {/* Add Another Vehicle Button */}
      {!disabled && vehicles.length > 0 && !showRegistrationForm && (
        <Button
          onClick={() => setShowRegistrationForm(true)}
          variant="ghost"
          className="w-full"
        >
          <Plus size={16} className="mr-2" />
          Adicionar outro veículo
        </Button>
      )}

      {/* Registration Form */}
      {showRegistrationForm && (
        <Card className="border-accent/20">
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Car className="text-accent" size={20} />
              <h3 className="font-semibold">Cadastrar Novo Veículo</h3>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">{settings.vehicleRegistrationLabel} *</label>
              <Input
                placeholder={settings.vehicleRegistrationLabel}
                mask="plate"
                value={newVehicle.placa}
                onChange={(e) => setNewVehicle(prev => ({
                  ...prev,
                  placa: e.target.value
                }))}
                disabled={loading}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Marca *</label>
              <Select
                value={newVehicle.marca}
                onChange={(e) => setNewVehicle(prev => ({ ...prev, marca: e.target.value, marca_custom: e.target.value === '__other__' ? prev.marca_custom : '' }))}
                disabled={loading}
              >
                <option value="">Selecione...</option>
                {marcas.map(marca => (
                  <option key={marca} value={marca}>{marca}</option>
                ))}
                <option value="__other__">Outra marca</option>
              </Select>
            </div>

            {newVehicle.marca === '__other__' && (
              <div>
                <label className="mb-1 block text-sm font-medium">Qual marca? *</label>
                <Input
                  placeholder="Digite a marca do veículo"
                  value={newVehicle.marca_custom}
                  onChange={(e) => setNewVehicle(prev => ({ ...prev, marca_custom: e.target.value }))}
                  disabled={loading}
                />
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium">Modelo *</label>
              <Input
                placeholder="Ex: Palio, Corolla, Gol..."
                value={newVehicle.modelo}
                onChange={(e) => setNewVehicle(prev => ({ ...prev, modelo: e.target.value }))}
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium">Ano *</label>
                <Select
                  value={newVehicle.ano}
                  onChange={(e) => setNewVehicle(prev => ({ ...prev, ano: e.target.value }))}
                  disabled={loading}
                >
                  <option value="">Selecione...</option>
                  {anos.map(ano => (
                    <option key={ano} value={ano}>{ano}</option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Cor</label>
                <Select
                  value={newVehicle.cor}
                  onChange={(e) => setNewVehicle(prev => ({ ...prev, cor: e.target.value }))}
                  disabled={loading}
                >
                  <option value="">Selecione...</option>
                  {cores.map(cor => (
                    <option key={cor} value={cor}>{cor}</option>
                  ))}
                </Select>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Odometro atual ({settings.measurementSystem === 'imperial' ? 'mi' : 'km'})</label>
              <Input
                placeholder="Ex: 45000"
                mask="integer"
                value={newVehicle.km_atual}
                onChange={(e) => setNewVehicle(prev => ({ ...prev, km_atual: onlyDigits(e.target.value, 7) }))}
                disabled={loading}
              />
            </div>

            <div className="flex flex-col-reverse gap-2 pt-4 sm:flex-row">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowRegistrationForm(false);
                  setNewVehicle({ placa: '', marca: '', marca_custom: '', modelo: '', ano: '', cor: '', km_atual: '' });
                  setFormError('');
                }}
                disabled={loading}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateVehicle}
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Cadastrando...' : 'Cadastrar Veículo'}
              </Button>
            </div>
            {formError && (
              <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
                {formError}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
